import React, { useState } from "react";
import { observerApi } from "../services/observerApi";
import { ObserverEntity } from "../types/observer";

type ProfileAvatarProps = {
  entity: ObserverEntity;
  size?: "small" | "large";
  linkToSource?: boolean;
};

export const ProfileAvatar = ({
  entity,
  size = "large",
  linkToSource = true,
}: ProfileAvatarProps) => {
  const [failedUrl, setFailedUrl] = useState("");
  const asset = entity.claimedAvatar || entity.avatar;
  const initials = entity.displayName.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  if (!asset || failedUrl === asset.url) {
    return (
      <span
        className={`ot-avatar ot-monogram ${size}`}
        aria-label={`${entity.displayName} monogram`}
      >
        {initials || "OT"}
      </span>
    );
  }

  const title = `${asset.attribution || "Source"}; refreshed ${new Date(asset.fetchedAt).toLocaleDateString("en-GB")}`;
  const content = (
    <>
      <img
        src={asset.url}
        alt={`${entity.displayName} channel avatar`}
        onError={() => setFailedUrl(asset.url)}
      />
      <small>{asset.attribution || "Source"}</small>
    </>
  );
  return linkToSource ? (
    <a
      className={`ot-avatar ${size}`}
      href={asset.sourceUrl}
      target="_blank"
      rel="noreferrer"
      title={title}
    >
      {content}
    </a>
  ) : (
    <span className={`ot-avatar ${size}`} title={title}>{content}</span>
  );
};

const compact = (value?: number) => value == null ? "Not available" : new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value);

export const FactStrip = ({ entity }: { entity: ObserverEntity }) => {
  const years = entity.channelStartedAt ? Math.max(0, new Date().getFullYear() - new Date(entity.channelStartedAt).getFullYear()) : undefined;
  const facts = [
    ["Subscribers", compact(entity.subscriberCount), "YouTube Data API, dated on the source record"],
    ["Total views", compact(entity.totalViewCount), "YouTube Data API, dated on the source record"],
    ["Videos", compact(entity.videoCount), "YouTube Data API, dated on the source record"],
    ["Last upload", entity.lastUploadAt ? new Date(entity.lastUploadAt).toLocaleDateString("en-GB") : "Not available", "Latest upload observed through the official API"],
    ["Years active", years == null ? "Not available" : `${years}+`, "Calculated from the official channel start date"],
  ];
  return <dl className="ot-fact-strip">{facts.map(([label, value, provenance]) => <div key={label} title={provenance}><dt>{label}</dt><dd>{value}</dd><small>Sourced fact ⓘ</small></div>)}</dl>;
};

export const ShareActions = ({ entity }: { entity: ObserverEntity }) => {
  const [message, setMessage] = useState("");
  const share = async (intent: "record" | "feedback") => {
    const data = await observerApi.shareLink(entity.id, intent);
    const text = `${data.copy} ${data.url}`;
    if (navigator.share) await navigator.share({ title: `${entity.displayName} · Observer Theory`, text, url: data.url }).catch(() => {});
    else await navigator.clipboard.writeText(text);
    setMessage("Share copy and the attributed short link are ready.");
  };
  return <div className="ot-share-actions"><button className="button button-ink" onClick={() => share("feedback")}>Share / ask for feedback</button><button className="button button-outline" onClick={() => share("record")}>Share independent record</button>{message && <small role="status">{message}</small>}</div>;
};
